// Documentation Types for WTF Amplify CRM

export type DocumentationCategory =
  | 'GETTING_STARTED'
  | 'AUTHENTICATION'
  | 'DASHBOARD'
  | 'PIPELINE'
  | 'LEADS'
  | 'TASKS'
  | 'DELIVERABLES'
  | 'RISK_ASSESSMENT'
  | 'DUPLICATES'
  | 'WHATSAPP'
  | 'ANALYTICS'
  | 'USER_MANAGEMENT'
  | 'TARGETS'
  | 'FINANCE'
  | 'SETTINGS'
  | 'FAQ';

export type UserRole = 'CEO' | 'GROUP_MANAGER' | 'MANAGER' | 'EXECUTIVE';

export interface DocumentationArticle {
  id: string;
  slug: string;
  title: string;
  summary?: string;
  content: string;
  category: DocumentationCategory;
  subcategory?: string;
  pageRoute?: string;
  featureKey?: string;
  visibleToRoles: UserRole[];
  sortOrder: number;
  parentId?: string;
  parent?: DocumentationArticle;
  children?: DocumentationArticle[];
  tags: string[];
  videoUrl?: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentationArticleListItem {
  id: string;
  slug: string;
  title: string;
  summary?: string;
  category: DocumentationCategory;
  subcategory?: string;
  pageRoute?: string;
  tags: string[];
  videoUrl?: string;
  sortOrder: number;
  updatedAt: string;
}

export interface DocumentationTip {
  id: string;
  elementId: string;
  pageRoute: string;
  title: string;
  content: string;
  learnMoreSlug?: string;
  visibleToRoles: UserRole[];
  position: 'top' | 'bottom' | 'left' | 'right';
  isActive: boolean;
}

export interface DocumentationSearchResult {
  id: string;
  slug: string;
  title: string;
  summary?: string;
  category: DocumentationCategory;
  subcategory?: string;
  pageRoute?: string;
  tags: string[];
  videoUrl?: string;
  sortOrder: number;
  updatedAt: string;
  relevanceScore?: number;
}

export interface DocumentationCategoryInfo {
  category: DocumentationCategory;
  count: number;
  label: string;
}

// API Response types
export interface DocumentationArticlesResponse {
  success: boolean;
  data: DocumentationArticleListItem[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface DocumentationArticleResponse {
  success: boolean;
  data: DocumentationArticle;
}

export interface DocumentationCategoriesResponse {
  success: boolean;
  data: DocumentationCategoryInfo[];
}

export interface DocumentationTipsResponse {
  success: boolean;
  data: DocumentationTip[];
}

export interface DocumentationSearchResponse {
  success: boolean;
  data: DocumentationSearchResult[];
  meta: {
    query: string;
    resultsCount: number;
  };
}

// Category display configuration
export interface CategoryConfig {
  key: DocumentationCategory;
  label: string;
  icon: string;
  description: string;
}

export const CATEGORY_CONFIGS: Record<string, CategoryConfig> = {
  GETTING_STARTED: {
    key: 'GETTING_STARTED',
    label: 'Getting Started',
    icon: 'Rocket',
    description: 'Introduction and quick start guides',
  },
  AUTHENTICATION: {
    key: 'AUTHENTICATION',
    label: 'Authentication',
    icon: 'Lock',
    description: 'Login, password reset, and security',
  },
  DASHBOARD: {
    key: 'DASHBOARD',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    description: 'Understanding your main dashboard',
  },
  PIPELINE: {
    key: 'PIPELINE',
    label: 'Pipeline',
    icon: 'GitBranch',
    description: 'Managing your sales pipeline',
  },
  LEADS: {
    key: 'LEADS',
    label: 'Leads',
    icon: 'Users',
    description: 'Creating and managing leads',
  },
  TASKS: {
    key: 'TASKS',
    label: 'Tasks',
    icon: 'CheckSquare',
    description: 'Task management and tracking',
  },
  DELIVERABLES: {
    key: 'DELIVERABLES',
    label: 'Deliverables',
    icon: 'Package',
    description: 'Client deliverables and proofs',
  },
  RISK_ASSESSMENT: {
    key: 'RISK_ASSESSMENT',
    label: 'Risk Assessment',
    icon: 'AlertTriangle',
    description: 'Identifying at-risk deals',
  },
  DUPLICATES: {
    key: 'DUPLICATES',
    label: 'Duplicates',
    icon: 'Copy',
    description: 'Duplicate detection and merging',
  },
  WHATSAPP: {
    key: 'WHATSAPP',
    label: 'WhatsApp',
    icon: 'MessageCircle',
    description: 'WhatsApp integration and messaging',
  },
  ANALYTICS: {
    key: 'ANALYTICS',
    label: 'Analytics',
    icon: 'BarChart2',
    description: 'Reports and analytics',
  },
  USER_MANAGEMENT: {
    key: 'USER_MANAGEMENT',
    label: 'User Management',
    icon: 'UserCog',
    description: 'Managing team members',
  },
  TARGETS: {
    key: 'TARGETS',
    label: 'Targets',
    icon: 'Target',
    description: 'Sales targets and goals',
  },
  FINANCE: {
    key: 'FINANCE',
    label: 'Finance',
    icon: 'IndianRupee',
    description: 'Payment tracking and finance',
  },
  SETTINGS: {
    key: 'SETTINGS',
    label: 'Settings',
    icon: 'Settings',
    description: 'Profile and preferences',
  },
  FAQ: {
    key: 'FAQ',
    label: 'FAQ',
    icon: 'HelpCircle',
    description: 'Frequently asked questions',
  },
};

// Helper to get category config
export const getCategoryConfig = (category: string): CategoryConfig => {
  return CATEGORY_CONFIGS[category] || {
    key: category as DocumentationCategory,
    label: category.replace(/_/g, ' '),
    icon: 'FileText',
    description: 'Documentation',
  };
};
